import React, { useState } from "react";
import api from "../lib/api";

const fmtErr = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" ");
  return d.msg || (d.code === "account_locked" ? "This account is locked." : "Something went wrong.");
};

const TITLES = { register: "Create Account", login: "Log In", forgot: "Reset Password", reset: "Enter Reset Code" };

function Field({ testId, ...props }) {
  return <input data-testid={testId} {...props}
                className="w-full h-11 px-3 rounded-lg bg-[#0A051A] border border-[#5B3BFF88] text-white font-mono text-sm outline-none focus:border-[#00F0FF] focus:shadow-[0_0_10px_#00F0FF55]" />;
}

// Email/password sign-up, log-in and code-based password reset. onDone(sessionResponse) after login/register.
export function EmailAuthModal({ onClose, onDone, upgrade = false }) {
  const [mode, setMode] = useState("register");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const go = (m) => { setMode(m); setMsg(null); setPw(""); };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      if (mode === "register" || mode === "login") {
        const r = await api.post(upgrade ? "/auth/upgrade/password" : `/auth/${mode}`, { email, password: pw });
        return onDone(r.data);
      }
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email });
        setMode("reset"); setPw("");
        setMsg({ ok: true, text: "If that email has a RING KINGS account, a 6-digit code is on its way." });
      } else {
        await api.post("/auth/reset-password", { email, code, new_password: pw });
        go("login");
        setMsg({ ok: true, text: "Password updated. Log in with your new password." });
      }
    } catch (err) {
      setMsg({ ok: false, text: fmtErr(err) });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose} data-testid="email-auth-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="lp-rise w-full max-w-[380px] rounded-2xl border-2 border-[#00F0FF] bg-[#120A2E] p-6 space-y-3 shadow-[0_0_40px_#00F0FF55]">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-black uppercase tracking-[0.15em] text-lg text-[#00F0FF]" data-testid="email-auth-title">{upgrade ? "Save Your Progress" : TITLES[mode]}</h2>
          <button type="button" onClick={onClose} className="text-[#A89BCC] hover:text-white font-mono" data-testid="email-auth-close">✕</button>
        </div>
        <Field testId="email-auth-email" type="email" placeholder="Email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} disabled={mode === "reset"} />
        {mode === "reset" && <Field testId="email-auth-code" inputMode="numeric" placeholder="6-digit code" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value)} />}
        {mode !== "forgot" && <Field testId="email-auth-password" type="password" minLength={8} required
                                     placeholder={mode === "reset" ? "New password (min. 8)" : mode === "register" ? "Password (min. 8)" : "Password"}
                                     autoComplete={mode === "login" ? "current-password" : "new-password"} value={pw} onChange={(e) => setPw(e.target.value)} />}
        {msg && <div data-testid="email-auth-msg" className="font-mono text-xs" style={{ color: msg.ok ? "#39FF14" : "#FF007F" }}>{msg.text}</div>}
        <button type="submit" disabled={busy} data-testid="email-auth-submit"
                className="w-full h-11 rounded-xl bg-[#00F0FF] text-[#0A051A] font-heading font-black uppercase tracking-[0.15em] text-sm transition-[box-shadow,transform,opacity] hover:shadow-[0_0_20px_#00F0FF] active:scale-95 disabled:opacity-50">
          {busy ? "…" : { register: "Create Account", login: "Log In", forgot: "Send Code", reset: "Set New Password" }[mode]}
        </button>
        {upgrade ? (
          <button type="button" data-testid="upgrade-google-btn"
                  onClick={() => {
                    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                    try { localStorage.setItem("rk_guest_upgrade", "1"); } catch {}
                    const redirectUrl = window.location.origin + "/roster";
                    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                  }}
                  className="w-full h-11 rounded-xl border-2 border-white/70 bg-white/5 font-heading font-bold uppercase tracking-[0.15em] text-xs text-white transition-colors hover:bg-white/15">
            Or save with Google
          </button>
        ) : (
        <div className="flex justify-between font-mono text-[11px] text-[#A89BCC]">
          {mode === "register"
            ? <button type="button" onClick={() => go("login")} className="hover:text-white" data-testid="email-auth-to-login">Have an account? Log in</button>
            : mode === "login"
              ? <button type="button" onClick={() => go("register")} className="hover:text-white" data-testid="email-auth-to-register">New here? Create account</button>
              : <button type="button" onClick={() => go("login")} className="hover:text-white" data-testid="email-auth-back-login">Back to log in</button>}
          {mode === "login" && <button type="button" onClick={() => go("forgot")} className="hover:text-white" data-testid="email-auth-to-forgot">Forgot password?</button>}
          {mode === "reset" && <button type="button" onClick={() => go("forgot")} className="hover:text-white" data-testid="email-auth-resend">Resend code</button>}
        </div>
        )}
      </form>
    </div>
  );
}
